import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { selectColumnsByFilterName } from '../../reducers/tableColumnsSlice';
import { useAppSelector } from '../../reducers/rootReducer';
import { updateSearchParams, compareArraysValues } from '../utils';
import { nullableFilters } from './useFiltersRestoreNew';
import type { LocalStorageAcceptableKeys } from '../types';
import { FarmOptionsTypes } from '../../components/CustomizeColumns/types';
import { checkForNullableFilters } from '../../components/CustomizeColumns/util';

export function useCustomizeColumnsDraft(
  filterTitle: string,
  data: FarmOptionsTypes.Selection[],
  tableName: string,
  localStorageKey: LocalStorageAcceptableKeys,
) {
  const navigate = useNavigate();
  const allSelectedColumns = useAppSelector((state) =>
    selectColumnsByFilterName(state, filterTitle),
  );
  const activeColumns = useMemo(
    () => allSelectedColumns?.map((item) => item.name) ?? [],
    [allSelectedColumns],
  );
  const [draft, setDraft] = useState<string[]>([]);

  useEffect(() => {
    setDraft((currentDraft) =>
      compareArraysValues(currentDraft, activeColumns)
        ? currentDraft
        : activeColumns,
    );
  }, [activeColumns]);

  const dirty = !compareArraysValues(activeColumns, draft);

  const apply = useCallback(() => {
    if (!data || data.length === 0) {
      return;
    }

    if (compareArraysValues(activeColumns, draft)) {
      return;
    }

    const newSelection = data.filter((element) => draft.includes(element.name));

    const newFilterColumns = checkForNullableFilters(
      tableName,
      filterTitle,
      newSelection,
    );

    updateSearchParams(
      filterTitle,
      newFilterColumns.join(','),
      false,
      navigate,
    );

    if (newFilterColumns.length === 0) {
      localStorage.updateObjectPropertyKey(
        localStorageKey,
        'filters',
        filterTitle,
        nullableFilters[filterTitle] ? 'null' : [],
      );
    }
  }, [
    activeColumns,
    data,
    draft,
    filterTitle,
    localStorageKey,
    navigate,
    tableName,
  ]);

  const discard = useCallback(() => {
    setDraft(activeColumns);
  }, [activeColumns]);

  return { draft, setDraft, dirty, apply, discard };
}

export default useCustomizeColumnsDraft;
